<template>
  <div class="gm-switch-button-container">
    <button
      v-if="isGameMaster"
      class="gm-switch-button"
      @click="openModal"
      :disabled="isSwitching"
      :title="'Switch Character (GM)'"
    >
      <div class="icon-wrapper">
        <img
          src="@/assets/icons/character_selector_v1.png"
          alt="Character Selector"
          class="button-icon"
        />
      </div>
      <span v-if="isSwitching" class="loading-indicator">
        <span class="loading-dots"></span>
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import GMCharacterSwitchService from '@/services/GMCharacterSwitchService';

const store = useStore();
const emit = defineEmits<{
  (e: 'openModal'): void;
}>();

const isSwitching = ref(false);

// Check if user is GM using Vuex store getter
const isGameMaster = computed(() => {
  return store.getters.isGameMaster;
});

const openModal = () => {
  console.debug('[GMSwitchButton] Opening character selector modal');
  emit('openModal');
};

// Event handlers
const handleSwitchStarted = () => {
  isSwitching.value = true;
};

const handleSwitchCompleted = () => {
  isSwitching.value = false;
};

const handleSwitchFailed = () => {
  isSwitching.value = false;
};

// Listen for service events
onMounted(() => {
  GMCharacterSwitchService.on('switchStarted', handleSwitchStarted);
  GMCharacterSwitchService.on('switchCompleted', handleSwitchCompleted);
  GMCharacterSwitchService.on('switchFailed', handleSwitchFailed);
});

// Cleanup listeners
onUnmounted(() => {
  GMCharacterSwitchService.off('switchStarted', handleSwitchStarted);
  GMCharacterSwitchService.off('switchCompleted', handleSwitchCompleted);
  GMCharacterSwitchService.off('switchFailed', handleSwitchFailed);
});
</script>

<style scoped>
.gm-switch-button-container {
  position: fixed;
  left: 20px;
  top: 80%;
  transform: translateY(-50%);
  z-index: 9999;
  pointer-events: none;
}

.gm-switch-button {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  pointer-events: auto;
  padding: 0;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
  animation: gentle-pulse 3s ease-in-out infinite;
}

.gm-switch-button:hover {
  transform: scale(1.15) rotate(5deg);
  filter: drop-shadow(0 6px 20px rgba(0, 0, 0, 0.4)) brightness(1.1);
  animation: none;
}

.gm-switch-button:active {
  transform: scale(1.05) rotate(2deg);
}

.gm-switch-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  animation: none;
}

.gm-switch-button:disabled:hover {
  transform: none;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
}

.icon-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.button-icon {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  -webkit-mask-image: radial-gradient(
    circle at center,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 1) 45%,
    rgba(0, 0, 0, 0.9) 60%,
    rgba(0, 0, 0, 0.6) 75%,
    rgba(0, 0, 0, 0.2) 90%,
    rgba(0, 0, 0, 0) 100%
  );
  mask-image: radial-gradient(
    circle at center,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 1) 45%,
    rgba(0, 0, 0, 0.9) 60%,
    rgba(0, 0, 0, 0.6) 75%,
    rgba(0, 0, 0, 0.2) 90%,
    rgba(0, 0, 0, 0) 100%
  );
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.gm-switch-button:hover .button-icon {
  transform: scale(1.05);
}

.loading-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  background: var(--gm-accent-hi);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--gm-surface-0);
  animation: pulse-indicator 1.5s ease-in-out infinite;
}

.loading-dots {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: white;
  position: relative;
  animation: loading-bounce 1.4s infinite ease-in-out;
}

.loading-dots::before,
.loading-dots::after {
  content: '';
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: white;
  animation: loading-bounce 1.4s infinite ease-in-out;
}

.loading-dots::before {
  left: -5px;
  animation-delay: -0.32s;
}

.loading-dots::after {
  left: 5px;
  animation-delay: -0.16s;
}

@keyframes gentle-pulse {
  0%, 100% {
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
  }
  50% {
    filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.4));
  }
}

@keyframes pulse-indicator {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes loading-bounce {
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
